import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Wrench, ChevronDown } from "lucide-react";
import { CONTACTS } from "@/data/site";

const LINES = ["HEAVY-DUTY", "DIESEL REPAIR", "& 24/7 TOWING"];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1 -mb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-end overflow-hidden carbon-texture">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src="/assets/tow-mixer-hero.jpg"
          alt="GSX Diesel Unit 01 heavy wrecker towing a cement mixer"
          className="w-full h-full object-cover object-[center_40%] opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-carbon via-carbon/55 to-carbon/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-carbon/85 via-carbon/30 to-transparent" />
      </motion.div>

      <div className="absolute top-24 right-6 hidden xl:block font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 [writing-mode:vertical-rl]">
        34.0538° N — 117.3965° W / Bloomington CA
      </div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-40 w-full">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="flex items-center gap-3 mb-8"
          data-testid="hero-badge"
        >
          <span className="w-2 h-2 bg-racing animate-pulse" />
          <span className="font-mono text-xs uppercase tracking-widest text-hazard font-semibold">
            Commercial Fleet Service • Inland Empire & I-10 / I-15 Corridor
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-heading uppercase font-black text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.9] text-white max-w-5xl"
        >
          <MaskedLine delay={0.35}>{LINES[0]}</MaskedLine>
          <MaskedLine delay={0.5}>
            <span className="chrome-text">{LINES[1]}</span>
          </MaskedLine>
          <MaskedLine delay={0.65}>
            <span className="text-racing italic">{LINES[2]}</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          data-testid="hero-subheadline"
        >
          Bloomington's elite commercial truck mechanics. Master computer diagnostics, engine
          rebuilds, transmissions, suspensions — and immediate heavy recovery when your rig goes down.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={CONTACTS.omar.tel}
            data-testid="hero-call-emergency-button"
            className="group flex items-center gap-3 px-8 py-4 bg-racing text-white font-heading font-extrabold uppercase text-lg tracking-wide emergency-pulse hover:bg-[#E01F1F] transition-colors duration-300"
          >
            <Phone className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
            Call 24/7 Towing Dispatch
          </a>
          <a
            href="#contact"
            data-testid="hero-book-service-button"
            className="group flex items-center gap-3 px-8 py-4 bg-hazard text-carbon font-heading font-extrabold uppercase text-lg tracking-wide hover:bg-white transition-colors duration-300"
          >
            <Wrench className="w-5 h-5 group-hover:-rotate-12 transition-transform duration-300" />
            Book Shop Service
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-16 flex items-center gap-2 text-slate-500 font-mono text-[10px] uppercase tracking-widest"
        >
          <ChevronDown className="w-4 h-4 animate-bounce" /> Scroll to explore the shop
        </motion.div>
      </motion.div>

      <div className="absolute bottom-0 inset-x-0 h-2 hazard-stripes" />
    </section>
  );
}
